import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, getAccessToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, RefreshCw, Download } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_BADGES = {
  uploaded:              { label: "Uploaded",       cls: "bg-slate-100 text-slate-700" },
  dry_run_running:       { label: "Validating…",    cls: "bg-blue-100 text-blue-700" },
  dry_run_ready:         { label: "Preview ready",  cls: "bg-blue-100 text-blue-700" },
  committing:            { label: "Committing…",    cls: "bg-amber-100 text-amber-700" },
  committed:             { label: "Committed",      cls: "bg-emerald-100 text-emerald-700" },
  committed_with_errors: { label: "Partial",        cls: "bg-amber-100 text-amber-700" },
  failed:                { label: "Failed",         cls: "bg-rose-100 text-rose-700" },
  failed_timeout:        { label: "Timed out",      cls: "bg-rose-100 text-rose-700" },
};

export default function ImportsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/imports?limit=100");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="imports-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Bulk imports</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Upload CSVs for rosters, worksites, dimensions, historical time
            entries, and 7 more record types. Every upload does a dry-run
            first — nothing writes to production until you review and
            confirm.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="imports-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button asChild data-testid="imports-new">
            <Link to="/app/admin/imports/new">
              <Upload className="h-3.5 w-3.5" /> New import
            </Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Recent jobs ({items.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No imports yet. Click "New import" to upload your first CSV.
                </TableCell></TableRow>
              )}
              {items.map((j) => {
                const s = STATUS_BADGES[j.status] || { label: j.status, cls: "bg-slate-100 text-slate-700" };
                return (
                  <TableRow key={j.id} data-testid={`import-row-${j.id}`}>
                    <TableCell className="font-medium capitalize">{j.type.replace(/_/g, " ")}</TableCell>
                    <TableCell className="max-w-[260px] truncate text-muted-foreground">
                      {j.original_filename || "—"}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {j.valid_rows}
                      <span className="text-muted-foreground"> / {j.total_rows}</span>
                      {j.error_rows > 0 && (
                        <Badge variant="outline" className="ml-2 text-rose-700">
                          {j.error_rows} err
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}
                            data-testid={`import-status-${j.id}`}>
                        {s.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {j.created_at ? formatDistanceToNow(new Date(j.created_at), { addSuffix: true }) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {j.error_rows > 0 && (
                          <Button asChild size="sm" variant="ghost" data-testid={`import-errors-${j.id}`}>
                            <a href={`${API}/tenant/imports/${j.id}/errors.csv?_t=${getAccessToken()?.slice(0,4)}`}
                               onClick={(e) => {
                                 e.preventDefault();
                                 // Use axios so bearer header is attached.
                                 api.get(`/tenant/imports/${j.id}/errors.csv`, { responseType: "blob" })
                                   .then(({ data }) => {
                                     const url = URL.createObjectURL(data);
                                     const a = document.createElement("a");
                                     a.href = url;
                                     a.download = `import-${j.type}-${j.id}-errors.csv`;
                                     a.click();
                                     URL.revokeObjectURL(url);
                                   });
                               }}>
                              <Download className="h-3.5 w-3.5" /> Errors
                            </a>
                          </Button>
                        )}
                        <Button asChild size="sm" variant="ghost">
                          <Link to={`/app/admin/imports/${j.id}`}>Open</Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
